import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Popover,
  Switch,
  Typography,
} from "@mui/material";

import { ThresholdWithDisplay } from "./hooks/useWasteThresholds";

type ThresholdRowProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  color: string;
  label: string;
  amount: string;
  isLast?: boolean;
};

function ThresholdRow({
  checked,
  onChange,
  color,
  label,
  amount,
  isLast = false,
}: ThresholdRowProps) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mb: isLast ? 0 : 0.5,
      }}
    >
      <FormControlLabel
        control={<Checkbox checked={checked} onChange={(e) => onChange(e.target.checked)} />}
        label={
          <Box component="span" sx={{ display: "flex", alignItems: "center" }}>
            <Box
              component="span"
              sx={{
                width: 20,
                height: 2,
                display: "inline-block",
                mr: 1,
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  width: "100%",
                  height: "2px",
                  background: `repeating-linear-gradient(to right, ${color} 0, ${color} 3px, transparent 2px, transparent 5px)`,
                },
              }}
            />
            {label}
          </Box>
        }
      />
      <Typography variant="body2" color="text.secondary">
        {amount}
      </Typography>
    </Box>
  );
}

type Props = {
  open: boolean;
  anchorEl: HTMLButtonElement | null;
  onClose: () => void;
  // Chart control states
  compareEPAWithVendor: boolean;
  setCompareEPAWithVendor: (value: boolean) => void;
  showHazardousWaste: boolean;
  setShowHazardousWaste: (value: boolean) => void;
  showAcutelyHazardousWaste: boolean;
  setShowAcutelyHazardousWaste: (value: boolean) => void;
  showStateRegulatedWaste: boolean;
  setShowStateRegulatedWaste: (value: boolean) => void;
  showGeneratorStatusBars: boolean;
  setShowGeneratorStatusBars: (value: boolean) => void;
  hasStateWasteThresholds: boolean;
  // Dynamic threshold states
  thresholds: ThresholdWithDisplay[];
  thresholdVisibility: Record<string, boolean>;
  setThresholdVisibility: (id: string, visible: boolean) => void;
  facilityState?: string | null;
};

export function ChartControlsPopover({
  open,
  anchorEl,
  onClose,
  compareEPAWithVendor,
  setCompareEPAWithVendor,
  showHazardousWaste,
  setShowHazardousWaste,
  showAcutelyHazardousWaste,
  setShowAcutelyHazardousWaste,
  showStateRegulatedWaste,
  setShowStateRegulatedWaste,
  showGeneratorStatusBars,
  setShowGeneratorStatusBars,
  hasStateWasteThresholds,
  thresholds,
  thresholdVisibility,
  setThresholdVisibility,
  facilityState,
}: Props) {
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      <Box sx={{ minWidth: 350 }}>
        <Box sx={{ px: 2, py: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={compareEPAWithVendor}
                onChange={(e) => setCompareEPAWithVendor(e.target.checked)}
              />
            }
            label="Compare EPA data with vendor data"
            sx={{ width: "100%" }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showGeneratorStatusBars}
                onChange={(e) => setShowGeneratorStatusBars(e.target.checked)}
              />
            }
            label="Show Generator Status bars"
            sx={{ width: "100%", mb: 0.5 }}
          />
        </Box>

        <Divider />

        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Waste Classification
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={showHazardousWaste}
                onChange={(e) => setShowHazardousWaste(e.target.checked)}
              />
            }
            label="Hazardous"
            sx={{ width: "100%", mb: 0.5 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={showAcutelyHazardousWaste}
                onChange={(e) => setShowAcutelyHazardousWaste(e.target.checked)}
              />
            }
            label="Acutely hazardous"
            sx={{ width: "100%", mb: 0.5 }}
          />

          {hasStateWasteThresholds && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={showStateRegulatedWaste}
                  onChange={(e) => setShowStateRegulatedWaste(e.target.checked)}
                />
              }
              label="State regulated"
              sx={{ width: "100%" }}
            />
          )}
        </Box>

        <Divider />

        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Federal Thresholds
          </Typography>
          {thresholds
            .filter((threshold) => threshold.type === "federal")
            .map((threshold, index, arr) => (
              <ThresholdRow
                key={threshold.id}
                checked={thresholdVisibility[threshold.id] ?? true}
                onChange={(checked) => setThresholdVisibility(threshold.id, checked)}
                color={threshold.color}
                label={threshold.name}
                amount={threshold.displayValue}
                isLast={index === arr.length - 1}
              />
            ))}
        </Box>

        <Divider />

        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            State Thresholds
          </Typography>
          {(() => {
            const stateThresholds = thresholds.filter((threshold) => threshold.type === "state");

            if (stateThresholds.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  There are no state thresholds for {facilityState || "this state"}
                </Typography>
              );
            }

            return stateThresholds.map((threshold, index) => (
              <ThresholdRow
                key={threshold.id}
                checked={thresholdVisibility[threshold.id] ?? true}
                onChange={(checked) => setThresholdVisibility(threshold.id, checked)}
                color={threshold.color}
                label={threshold.name}
                amount={threshold.displayValue}
                isLast={index === stateThresholds.length - 1}
              />
            ));
          })()}
        </Box>
      </Box>
    </Popover>
  );
}
